import React, { useState } from "react";
import styled, { ThemeProvider } from "styled-components";

import {
  LightGlobalStyle,
  DarkGlobalStyle,
  makeTheme,
  makeColorScale,
  Paragraph,
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  SmallText,
  Button,
  BorderButton,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Flex,
  Tag,
  TextInput,
  TextArea,
  Checkbox,
  Radio,
  Hero,
  HeroTag,
  HeroTitle,
  HeroText,
  List
} from "./lib";

const RaisingLetter = styled.span`
  display: inline-block;
  transition: transform 200ms ease;
  &:hover {
    transform: translateY(8px);
  }
`;

const PianoText = ({ text }: { text: string }) => (
  <H1>
    {text.split("").map((letter, index) => (
      <RaisingLetter key={index}>{letter}</RaisingLetter>
    ))}
  </H1>
);

const Demo = ({ params }: { params: any }) => {
  const [theme, setTheme] = useState("light");

  const MyLightTheme = makeTheme({
    primaryColor: params.primaryColor || "mediumblue",
    isDark: false,
    baseFontSize: params.baseFontSize || 22,
    textScale: params.textScale || 1.33
  });

  const MyDarkTheme = makeTheme({
    primaryColor: params.primaryColor || "royalblue",
    isDark: true,
    baseFontSize: params.baseFontSize || 22,
    textScale: params.textScale || 1.33
  });

  const theTheme = theme === "light" ? MyLightTheme : MyDarkTheme;

  return (
    <ThemeProvider theme={theTheme}>
      {theme === "light" ? <LightGlobalStyle /> : <DarkGlobalStyle />}
      <Flex
        direction="column"
        align="flex-start"
        style={{ padding: 40, flexGrow: 1 }}
      >
        <header style={{ width: "100%" }}>
          <Flex justify="space-between">
            <PianoText text={"Piano"} />
            <Button
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
            >
              Swap Theme
            </Button>
          </Flex>

          <Paragraph>
            A widely reusable <strong>React</strong> based design system
          </Paragraph>
        </header>
        <H2>Typographic Scale</H2>

        <hr />

        <H1>Heading 1</H1>
        <H2>Heading 2</H2>
        <H3>Heading 3</H3>
        <H4>Heading 4</H4>
        <H5>Heading 5</H5>
        <H6>Heading 6</H6>

        <Paragraph>Paragraph text, commonly used.</Paragraph>
        <SmallText>Small text, use sparingly.</SmallText>
        <hr />
        <Hero align="center" style={{ textAlign: "center" }}>
          <HeroTag>HERO</HeroTag>
          <HeroTitle>This is a heading</HeroTitle>
          <HeroText style={{ maxWidth: 1000 }}>
            This is the accompanying paragraph which will look and feel just
            right in order to work well with what's around it. Even the button
            below gets sized up to call attention to it.
          </HeroText>
          <Button>Click Me</Button>
        </Hero>
        <hr />
        <hr />
        <Hero>
          <HeroTitle>Hero Title</HeroTitle>
          <HeroText>
            This hero section only has a title and some text. No worries.
          </HeroText>
        </Hero>
        <hr />
        <H2>Buttons</H2>
        <Flex gap="0.5rem" style={{ marginBottom: "3rem" }}>
          <Button>Primary Button</Button>
          <BorderButton>Outline Button</BorderButton>
          <BorderButton color="danger">Destructive Button</BorderButton>
          <BorderButton color="inverse">Inverse Button</BorderButton>
        </Flex>

        <H2>Tags</H2>
        <Flex gap="0.5rem" style={{ marginBottom: "3rem" }}>
          <Tag>Hello Tag</Tag>
          <Tag color="inverse">Hello Tag</Tag>
          <Tag color="danger">Hello Tag</Tag>
        </Flex>
        <hr />
        <H2>Cards</H2>
        <Card padding style={{ width: 256, textAlign: "center" }}>
          <h2>Hello Card</h2>
        </Card>
        <Card padding style={{ width: 256, textAlign: "center" }}>
          <h2>Hello Card</h2>
        </Card>
        <Card style={{ width: 256, textAlign: "center" }}>
          <CardHeader>Header</CardHeader>
          <CardBody>This is the body of the card.</CardBody>
          <CardFooter>
            <Button>Test</Button>
          </CardFooter>
        </Card>

        <hr />
        <Flex gap="0.5rem" style={{ marginBottom: "1rem" }}>
          <TextInput />
          <Button>Submit</Button>
        </Flex>

        <Flex gap="0.5rem" style={{ marginBottom: "1rem" }}>
          <TextArea />
        </Flex>

        <Flex style={{ marginBottom: "1rem" }}>
          <Checkbox />
        </Flex>

        <Flex style={{ marginBottom: "3rem" }}>
          <Radio />
        </Flex>

        <H2>List</H2>
        <List options={["Option 1", "Option 2"]} />

        <hr />

        <H2>Make a Color Scale</H2>
        <Flex gap="12px">
          {makeColorScale("red", 11).map((color, index) => (
            <div
              key={index}
              style={{
                width: 48,
                height: 48,
                border: "4px solid white",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                borderRadius: 32,
                backgroundColor: color
              }}
            />
          ))}
        </Flex>

        <Flex gap="12px">
          {makeColorScale("black", 11).map((color, index) => (
            <div
              key={index}
              style={{
                width: 48,
                height: 48,
                border: "4px solid white",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                borderRadius: 32,
                backgroundColor: color
              }}
            />
          ))}
        </Flex>
      </Flex>
    </ThemeProvider>
  );
};

export default Demo;
